import { z } from "zod";
import { defineStepConfig } from "../registry";
import { SystemEntity, SystemEvent } from "@erp/shared-types";
import { entityOfType } from "../utils/system-entities";
import { workflowExecutionTimeoutSchema } from "../utils/workflowExecutionTimeout";

const inputSchema = z.never();

const configSchema = z.object({
  emailRuleIds: z
    .array(z.uuid())
    .min(1)
    .describe("Email rule IDs that trigger this workflow"),
  skipSelfSent: z
    .boolean()
    .default(true)
    .describe(
      "When true, do not trigger this workflow for outbound emails that loop back into our own inbox (i.e. emails the system sent that re-appeared as inbound). Does not affect replies from external parties. Defaults to true.",
    ),
  workflowExecutionTimeout: workflowExecutionTimeoutSchema,
});

export type EmailRuleTriggerConfig = z.infer<typeof configSchema>;

const outputSchema = z.object({
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
    "The inbound email that matched the rule",
  ),
  documents: z
    .array(entityOfType(SystemEntity.CORE_DOCUMENT))
    .optional()
    .describe("Documents (attachments + email body) associated with the email"),
  from: z.string().describe("Sender email address"),
  to: z.string().describe("Recipient email address(es)"),
  cc: z.string().optional().describe("CC email address(es)"),
  subject: z.string().optional().describe("Email subject line"),
  emailReceivedAt: z.iso
    .datetime({ offset: true })
    .nullish()
    .describe("Provider-supplied mailbox receipt timestamp, when known"),
  conversationId: z
    .string()
    .optional()
    .describe("Email conversation/thread ID"),
});

const emits = [] as const;

export const emailRuleTriggerStep = defineStepConfig({
  type: "email_rule_trigger",
  name: "Email Rule Trigger",
  description:
    "Entry point for workflows triggered by an inbound email matching an email rule",
  category: "Trigger",
  inputSchema,
  outputSchema,
  configSchema,
  emits,
});

/**
 * The system event type this trigger subscribes to internally.
 * Used by the orchestration layer to route EMAIL_ROUTED_EMAIL_RULE events
 * to workflows using either the legacy event_trigger or the new email_rule_trigger.
 */
export const EMAIL_RULE_TRIGGER_EVENT_TYPE =
  SystemEvent.EMAIL_ROUTED_EMAIL_RULE;
